import Grid from '@mui/material/Grid';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import classNames from 'classnames';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useStoreActions, useStoreState } from '~/store/hooks';
import { getLocalDateNoFormat, setNoon } from '~/utils/date/date';
import { extractErrorMessage } from '~/utils/error/error';
import { IClientDetailedV3 } from '~/services/api/client/types';
import { IClientStatus } from '~/services/api/types';
import Button from '~/ui/components/common/Button';
import DatePickerNew from '~/ui/components/inputs/DatePickerNew';
import Select from '~/ui/components/inputs/SelectWithoutAnimation';
import normalizeClientStatusDates from '~/ui/pages/MedicationLogistics/helpers/normalizeClientStatusDates';

import { statusOptions } from '~/ui/pages/Clients/constants/statusOptions';
import ClientStatus from '~/ui/pages/Clients/models/clientStatus';
import StatusHistoryItem from './StatusHistoryItem';

import styles from './Status.module.scss';

interface IProps {
  client: IClientDetailedV3;
  clinicId?: string;
  actTeamId?: string;
}

const Status = ({ client, clinicId, actTeamId }: IProps): ReactElement => {
  const [showHistory, setShowHistory] = useState(false);

  const {
    status: { current, previous: previousStatuses },
  } = useStoreState(state => state.clientDetails) || {};

  const previous = normalizeClientStatusDates(previousStatuses, current);

  const { showError } = useStoreActions(actions => actions.snackbar);
  const { onChangeClientStatus, onChangeClientStatusDate, onGetClientStatus } = useStoreActions(
    actions => actions.clientDetails,
  );

  const initialDate =
    current?.value === ClientStatus.ACTIVE ? current?.dateOfRelease : current?.dateOfEntry;

  const {
    formState: { errors, dirtyFields },
    control,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      status: current?.value || null,
      date: initialDate ? new Date(setNoon(initialDate)) : null,
    },
  });

  const { status } = watch();

  const isCurrentStatusActive = current?.value === ClientStatus.ACTIVE && status === current?.value;

  const isStatusActive = status === ClientStatus.ACTIVE;
  const isSameStatus = status === current?.value;

  // send change client status request
  const onSubmit = async (newDate: Date) => {
    newDate.setHours(12);
    if (newDate.toISOString() === initialDate && status === current?.value) {
      return;
    }

    const formattedDate = newDate.toISOString().split('T')[0];
    try {
      const requestInfo = { clinicId, teamId: actTeamId, clientId: String(client.id) };
      const payload = {
        requestInfo,
        status: {
          value: status,
          dateOfEntry: !isStatusActive ? formattedDate : undefined,
          dateOfRelease: isStatusActive ? formattedDate : undefined,
        },
      };
      if (status === current?.value) {
        await onChangeClientStatusDate({ requestInfo, dateOfEntry: formattedDate });
      } else {
        await onChangeClientStatus(payload);
      }
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const onCancel = () => {
    setValue('date', initialDate ? new Date(setNoon(initialDate)) : null);
    setValue('status', status || current?.value || null);
  };

  const onMount = useCallback(async () => {
    const isUserArchived =
      client.actTeams.find(team => team.team.id === Number(actTeamId))?.isArchived ||
      client.isArchived;

    if (Number(clinicId) && actTeamId && client.id && !isUserArchived) {
      try {
        await onGetClientStatus({ clinicId, teamId: actTeamId, clientId: String(client.id) });
      } catch (e) {
        showError(extractErrorMessage(e));
      }
    }
  }, [client, clinicId, actTeamId, onGetClientStatus, showError]);

  // get client current status and history on mount
  useEffect(() => {
    onMount();
  }, [onMount]);

  // open date picker on status change
  useEffect(() => {
    if (dirtyFields.status) {
      setValue('date', null);
      const picker = document.getElementsByClassName(
        'controlledDatePicker',
      )?.[0] as HTMLButtonElement;
      if (picker) {
        picker.click();
      }
    }
    // should run effect only on status change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, dirtyFields]);

  const getIcon = (expanded: boolean) =>
    !expanded ? <ExpandMoreIcon color="primary" /> : <ExpandLessIcon color="primary" />;

  const renderItem = (item: IClientStatus, index: number) => (
    <StatusHistoryItem item={item} key={item?.value + index} isFirst={!index} />
  );

  const calendarPlaceholder = `Date of ${status === ClientStatus.ACTIVE ? 'Release' : 'Entry'}`;
  const calendarTitle = `Please enter the ${calendarPlaceholder}`;
  const minDate = isStatusActive
    ? getLocalDateNoFormat(current?.dateOfEntry || new Date()).toISOString()
    : getLocalDateNoFormat(
        current?.dateOfEntry || current?.dateOfRelease || new Date(),
      ).toISOString();

  return (
    <div className={styles.wrapper}>
      <div className={styles.innerWrapper}>
        <div className={classNames(styles.row, styles.topBlock)}>
          <h4 className={styles.title}>Status</h4>
        </div>
        <Grid container spacing={2} className={styles.form}>
          <Grid
            size={{
              sm: 6,
            }}
          >
            <Select
              name="status"
              options={statusOptions}
              control={control}
              errors={errors}
              label="Status"
              hideSelectedOptions={false}
            />
          </Grid>

          <Grid
            size={{
              sm: 6,
            }}
          >
            {!isCurrentStatusActive && (
              <DatePickerNew
                control={control}
                errors={errors}
                name="date"
                label={calendarPlaceholder}
                className="controlledDatePicker"
                maxDate={new Date().toISOString()}
                minDate={
                  isSameStatus || (!current?.dateOfEntry && !current?.dateOfRelease)
                    ? undefined
                    : minDate
                }
                title={calendarTitle}
                inputDisabled
                withConfirmation
                onCancel={onCancel}
                onSubmit={onSubmit}
              />
            )}
          </Grid>
        </Grid>
        <div className={styles.historyList}>
          {!!previous.length && (
            <Button
              variant="text"
              endIcon={getIcon(showHistory)}
              onClick={() => setShowHistory(prev => !prev)}
              color="primary"
              className={styles.showMoreButton}
            >
              {`${showHistory ? 'Hide' : 'Show'} Prior Statuses`}
            </Button>
          )}
          {showHistory && !!previous.length && previous.map(renderItem)}
        </div>
      </div>
    </div>
  );
};

export default Status;
